import type { ReactNode } from "react"

import cx from "classnames"

import { badgeColors, type BadgeColor } from "./config"
import styles from "./index.module.css"

/**
 * Props for the Badge component.
 */
export type BadgeProps = {
  /**
   * The content to display in the badge.
   * If not provided, only a dot is shown.
   */
  children?: ReactNode

  /**
   * The color scheme of the badge.
   */
  color?: BadgeColor

  /**
   * Optional icon to display before the content.
   */
  icon?: ReactNode
}

export { badgeColors } from "./config"

const colorClass = {
  [badgeColors.DEFAULT]: styles.colorDefault,
  [badgeColors.INFO]: styles.colorInfo,
  [badgeColors.SUCCESS]: styles.colorSuccess,
  [badgeColors.WARNING]: styles.colorWarning,
  [badgeColors.ERROR]: styles.colorError,
}

/**
 * A badge component for displaying notification indicators.
 * @param {BadgeProps} props - The props for the Badge component.
 * @returns {React.JSX.Element} - A React JSX element representing the badge.
 */
export const Badge = ({ children, color = badgeColors.ERROR, icon }: BadgeProps): React.JSX.Element => {
  const hasContent = children !== undefined && children !== null && children !== ""
  const hasIcon = icon !== undefined && icon !== null

  const badgeClasses = cx(styles.badge, colorClass[color], {
    [styles.dot]: !hasContent && !hasIcon,
  })

  return (
    <span className={badgeClasses}>
      {hasIcon && <span className={styles.icon}>{icon}</span>}
      {hasContent && <span className={styles.text}>{children}</span>}
    </span>
  )
}
